import { useEffect, useRef, useState, useCallback } from 'react';
import { AutoRefreshConfig } from './useAutoRefreshConfig';

interface UseAutoRefreshProps {
  config: AutoRefreshConfig;
  expandedProjectIds: number[];
  onRefreshProjects: (projectIds: number[]) => Promise<void>;
}

export function useAutoRefresh({ config, expandedProjectIds, onRefreshProjects }: UseAutoRefreshProps) {
  const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout>();
  const pauseTimeoutRef = useRef<NodeJS.Timeout>();

  // Main auto-refresh interval
  useEffect(() => {
    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Don't start interval if disabled, paused, or no projects expanded
    if (!config.enabled || isPaused || expandedProjectIds.length === 0) {
      return;
    }

    console.log(`Starting auto-refresh for ${expandedProjectIds.length} expanded projects (${config.intervalSeconds}s interval)`);
    
    intervalRef.current = setInterval(async () => {
      if (expandedProjectIds.length > 0) {
        console.log('Auto-refreshing expanded projects:', expandedProjectIds);
        try {
          await onRefreshProjects(expandedProjectIds);
        } catch (error) {
          console.warn('Auto-refresh failed:', error);
        }
      }
    }, config.intervalSeconds * 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [config.enabled, config.intervalSeconds, isPaused, expandedProjectIds, onRefreshProjects]);

  // Pause auto-refresh temporarily
  const pauseAutoRefresh = useCallback((resumeAfterMs: number = 3000) => {
    console.log(`Pausing auto-refresh for ${resumeAfterMs}ms`);
    setIsPaused(true);
    
    // Clear any existing resume timeout
    if (pauseTimeoutRef.current) {
      clearTimeout(pauseTimeoutRef.current);
    }
    
    // Schedule resume
    pauseTimeoutRef.current = setTimeout(() => {
      console.log('Resuming auto-refresh after user inactivity');
      setIsPaused(false);
    }, resumeAfterMs);
  }, []);

  // Resume auto-refresh immediately
  const resumeAutoRefresh = useCallback(() => {
    console.log('Resuming auto-refresh immediately');
    if (pauseTimeoutRef.current) {
      clearTimeout(pauseTimeoutRef.current);
    }
    setIsPaused(false);
  }, []);

  // Handle context menu interactions
  useEffect(() => {
    const handleContextMenu = () => {
      pauseAutoRefresh(3000); // Pause for 3 seconds after context menu
    };

    document.addEventListener('contextmenu', handleContextMenu);
    return () => document.removeEventListener('contextmenu', handleContextMenu);
  }, [pauseAutoRefresh]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (pauseTimeoutRef.current) {
        clearTimeout(pauseTimeoutRef.current);
      }
    };
  }, []);

  return {
    isPaused,
    pauseAutoRefresh,
    resumeAutoRefresh,
  };
}